import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Box, Button, Divider, Stack, Tab, styled } from '@mui/material';

import { MasterNameApi } from '@/api';
import { FormAddressField } from '@/components/FormElements/FormAddressField';
import { FormCheckboxField } from '@/components/FormElements/FormCheckboxField';
import { FormDateField } from '@/components/FormElements/FormDateField';
import { FormInputField } from '@/components/FormElements/FormInputField';
import { FormSelectField } from '@/components/FormElements/FormSelectField';
import { SelectItem } from '@/components/FormElements/Select';
import { AppModal } from '@/components/Modals/AppModal';
import { Tabs } from '@/components/Tabs';
import {
  IAddress,
  IAddressForm,
  IMasterName,
  IMasterNameOrganization,
  IMasterNamePerson,
  IMasterNameReq,
  IMasterOrganizationRequest,
  IOrganizationType,
  MasterNameType,
  OrganizationType,
} from '@/models';
import { ConfirmTypes, useToastStore } from '@/store';
import { parsePhoneNumber } from '@/utils';

export type ReporterModalTab = 'person' | 'organization';

interface ReporterDetailsModalProps {
  open: boolean;
  onClose: () => void;
  defaultTab?: ReporterModalTab;
  mode?: 'add' | 'update';
  initialValue?: IMasterName;
  onSave: (masterName: IMasterName) => Promise<void> | void;
}

interface PersonFormValues {
  firstName: string;
  lastName: string;
  address: IAddressForm | null;
  dateOfBirth: string;
  isVerified: boolean;
}

interface OrganizationFormValues {
  organizationName: string;
  organizationType: IOrganizationType | '';
  address: IAddressForm | null;
  isVerified: boolean;
}

const ORGANIZATION_TYPE_ITEMS: SelectItem[] = Object.entries(
  OrganizationType,
).map(([value, label]) => ({
  label,
  value,
}));

const ReporterDetailsModalRoot = styled(AppModal)(() => ({
  '& .modal-content': {
    width: 720,
    maxWidth: '90vw',
    overflow: 'visible',
  },
  '& .divider': {
    marginTop: '0 !important',
    marginBottom: '0 !important',
  },
  '& .MuiAutocomplete-paper': {
    maxHeight: 240,
    overflowY: 'auto',
  },
  '& .MuiAutocomplete-listbox': {
    maxHeight: 240,
    overflowY: 'auto',
  },
}));

const FormGrid = styled('div')(() => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  columnGap: 16,
  rowGap: 4,
}));

const StyledTab = styled(Tab)(() => ({
  opacity: 0.65,
  '&.Mui-selected': {
    opacity: 1,
  },
}));

const FieldsRoot = styled('div')(() => ({
  '& > *': {
    marginBottom: '0 !important',
  },
  '& .MuiInputBase-root': {
    height: 32,
    minHeight: 32,
    padding: '0 10px',
  },
  '& .MuiInputBase-input': {
    padding: 0,
  },
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
  },
}));

const FullRow = styled('div')(() => ({
  gridColumn: '1 / -1',
  '& > *': {
    marginBottom: '0 !important',
  },
}));

export const ReporterDetailsModal = ({
  open,
  onClose,
  defaultTab = 'person',
  mode = 'add',
  initialValue,
  onSave,
}: ReporterDetailsModalProps) => {
  const { updateToast } = useToastStore();
  const isUpdate = mode === 'update';
  const [activeTab, setActiveTab] = useState<ReporterModalTab>(defaultTab);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const lastInitKeyRef = useRef<string>('');

  const personForm = useForm<PersonFormValues>({
    shouldUnregister: false,
    mode: 'onSubmit',
    defaultValues: {
      firstName: '',
      lastName: '',
      address: null,
      dateOfBirth: '',
      isVerified: false,
    },
  });

  const organizationForm = useForm<OrganizationFormValues>({
    shouldUnregister: false,
    mode: 'onSubmit',
    defaultValues: {
      organizationName: '',
      organizationType: '',
      address: null,
      isVerified: false,
    },
  });

  useEffect(() => {
    if (!open) {
      lastInitKeyRef.current = '';
      return;
    }

    const initKey = `${isUpdate}-${initialValue?._id ?? 'new'}-${defaultTab}`;
    if (lastInitKeyRef.current === initKey) return;
    lastInitKeyRef.current = initKey;

    if (isUpdate && initialValue) {
      const nextTab =
        initialValue.type === MasterNameType.ORGANIZATION
          ? 'organization'
          : 'person';
      setActiveTab(nextTab);
      const addressValue =
        initialValue.addresses?.[0] || initialValue.address || null;

      if (nextTab === 'person') {
        const personValue = initialValue as IMasterNamePerson;
        personForm.reset({
          firstName: personValue.firstName ?? '',
          lastName: personValue.lastName ?? '',
          address: addressValue as IAddressForm | null,
          dateOfBirth: personValue.birthday ?? '',
          isVerified: Boolean(personValue.isVerified),
        });
      } else {
        const organizationValue = initialValue as IMasterNameOrganization;
        organizationForm.reset({
          organizationName: organizationValue.name ?? '',
          organizationType: organizationValue.organizationType ?? '',
          address: addressValue as IAddressForm | null,
          isVerified: Boolean(organizationValue.isVerified),
        });
      }
    } else {
      setActiveTab(defaultTab);
      personForm.reset({
        firstName: '',
        lastName: '',
        address: null,
        dateOfBirth: '',
        isVerified: false,
      });
      organizationForm.reset({
        organizationName: '',
        organizationType: '',
        address: null,
        isVerified: false,
      });
    }
  }, [
    open,
    defaultTab,
    isUpdate,
    initialValue?._id,
    initialValue?.isVerified,
    organizationForm,
    personForm,
  ]);

  const handleClose = () => {
    personForm.reset();
    organizationForm.reset();
    onClose();
  };

  const handleAdd = async () => {
    if (isSubmitting) return;
    try {
      setIsSubmitting(true);
      if (activeTab === 'person') {
        const trimmedFirstName = personForm.getValues('firstName').trim();
        const trimmedLastName = personForm.getValues('lastName').trim();
        const trimmedDateOfBirth = (
          personForm.getValues('dateOfBirth') ?? ''
        ).trim();
        personForm.setValue('firstName', trimmedFirstName, {
          shouldValidate: true,
        });
        personForm.setValue('lastName', trimmedLastName, {
          shouldValidate: true,
        });
        personForm.setValue('dateOfBirth', trimmedDateOfBirth, {
          shouldValidate: true,
        });
        const isValid = await personForm.trigger();
        if (!isValid) return;

        const values = personForm.getValues();
        const addressValue = values.address as IAddress | null;
        let result: IMasterName;
        if (isUpdate) {
          const base = initialValue as IMasterNamePerson | undefined;
          if (!base?._id) return;
          const {
            createdAt,
            updatedAt,
            type,
            address,
            driverLicense,
            employerInfo,
            ...rest
          } = base;
          const payload: IMasterNameReq = {
            ...rest,
            employerInfo: {
              ...employerInfo,
              address: employerInfo?.address?._id,
              phoneNumber: parsePhoneNumber(employerInfo?.phoneNumber, '+1'),
            },
            firstName: trimmedFirstName,
            lastName: trimmedLastName,
            birthday: trimmedDateOfBirth,
            address: addressValue?._id ?? '',
            addresses: addressValue ? [addressValue] : [],
            driverLicense: driverLicense?._id,
            isVerified: values.isVerified,
          } as IMasterNameReq;
          const res = await MasterNameApi.updateMasterName(payload);
          result = res.data;
        } else {
          const payload: IMasterNameReq = {
            firstName: trimmedFirstName,
            lastName: trimmedLastName,
            birthday: trimmedDateOfBirth,
            address: '',
            addresses: addressValue ? [addressValue] : [],
            isVerified: values.isVerified,
          } as IMasterNameReq;
          const res = await MasterNameApi.createMasterNamePerson(payload);
          result = res.data;
        }
        await onSave(result);
        updateToast({
          open: true,
          type: ConfirmTypes.SUCCESS,
          message: isUpdate
            ? 'Reporter updated successfully.'
            : 'Reporter added successfully.',
        });
      } else {
        const trimmedOrganizationName = organizationForm
          .getValues('organizationName')
          .trim();
        organizationForm.setValue('organizationName', trimmedOrganizationName, {
          shouldValidate: true,
        });
        const isValid = await organizationForm.trigger();
        if (!isValid) return;

        const values = organizationForm.getValues();
        const addressValue = values.address as IAddress | null;
        let result: IMasterNameOrganization;
        if (isUpdate) {
          const base = initialValue as IMasterNameOrganization | undefined;
          if (!base?._id) return;
          const { address, ...rest } = base;
          const payload: IMasterOrganizationRequest = {
            ...rest,
            name: trimmedOrganizationName,
            type: MasterNameType.ORGANIZATION,
            organizationType: values.organizationType || undefined,
            address: addressValue?._id ?? '',
            addresses: addressValue ? [addressValue] : [],
            isVerified: values.isVerified,
          } as IMasterOrganizationRequest;
          const res = await MasterNameApi.updateMasterNameOrganization(payload);
          result = res.data;
        } else {
          const payload: IMasterOrganizationRequest = {
            name: trimmedOrganizationName,
            type: MasterNameType.ORGANIZATION,
            organizationType: values.organizationType || undefined,
            address: addressValue?._id,
            addresses: addressValue ? [addressValue] : undefined,
            isVerified: values.isVerified,
          } as IMasterOrganizationRequest;
          const res = await MasterNameApi.createMasterNameOrganization(payload);
          result = res.data;
        }
        await onSave(result);
        updateToast({
          open: true,
          type: ConfirmTypes.SUCCESS,
          message: isUpdate
            ? 'Reporter updated successfully.'
            : 'Reporter added successfully.',
        });
      }
      handleClose();
    } catch (err: any) {
      updateToast({
        open: true,
        type: ConfirmTypes.ERROR,
        message: err.message || 'Failed to add reporter.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ReporterDetailsModalRoot
      open={open}
      title={isUpdate ? 'Update details' : 'Add details'}
      onClose={handleClose}
    >
      <Stack spacing={2}>
        <Stack spacing={0} sx={{ pb: 0 }}>
          <Tabs
            value={activeTab}
            onChange={isUpdate ? undefined : (_, value) => setActiveTab(value)}
          >
            <StyledTab
              label="Person"
              value="person"
              disabled={isUpdate && activeTab !== 'person'}
            />
            <StyledTab
              label="Organization"
              value="organization"
              disabled={isUpdate && activeTab !== 'organization'}
            />
          </Tabs>
          <Divider sx={{ mt: 0 }} />
        </Stack>

        <Box sx={{ display: activeTab === 'person' ? 'block' : 'none' }}>
          <FormProvider {...personForm}>
            <FieldsRoot>
              <FormGrid>
                <FormInputField
                  name="lastName"
                  label="Last name"
                  rules={{
                    required: 'Last name is required.',
                    maxLength: {
                      value: 50,
                      message: 'Last name must be 50 characters or less.',
                    },
                  }}
                />
                <FormInputField
                  name="firstName"
                  label="First name"
                  rules={{
                    required: 'First name is required.',
                    maxLength: {
                      value: 50,
                      message: 'First name must be 50 characters or less.',
                    },
                  }}
                />
                <FullRow>
                  <FormAddressField
                    name="address"
                    label="Address"
                    defaultValue={null}
                    showGpsIcon={false}
                  />
                </FullRow>
                <FormDateField name="dateOfBirth" label="Date of birth" />
                <FullRow>
                  <FormCheckboxField name="isVerified" label="Verified" />
                </FullRow>
              </FormGrid>
            </FieldsRoot>
          </FormProvider>
        </Box>

        <Box sx={{ display: activeTab === 'organization' ? 'block' : 'none' }}>
          <FormProvider {...organizationForm}>
            <FieldsRoot>
              <FormGrid>
                <FormInputField
                  name="organizationName"
                  label="Organization name"
                  rules={{
                    required: 'Organization name is required.',
                    maxLength: {
                      value: 50,
                      message:
                        'Organization name must be 50 characters or less.',
                    },
                  }}
                />
                <FormSelectField
                  name="organizationType"
                  label="Organization type"
                  items={ORGANIZATION_TYPE_ITEMS}
                />
                <FullRow>
                  <FormAddressField
                    name="address"
                    label="Address"
                    defaultValue={null}
                    showGpsIcon={false}
                  />
                </FullRow>
                <FullRow>
                  <FormCheckboxField name="isVerified" label="Verified" />
                </FullRow>
              </FormGrid>
            </FieldsRoot>
          </FormProvider>
        </Box>

        <Divider sx={{ mt: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" color="inherit" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleAdd}
              disabled={isSubmitting}
            >
              {isUpdate ? 'Update' : 'Add'}
            </Button>
          </Stack>
        </Box>
      </Stack>
    </ReporterDetailsModalRoot>
  );
};
